// bodyRegions.ts
// Anatomical region coordinates (100×150 viewBox) and location-to-region mapping.

export interface BodyRegion {
  x: number;
  y: number;
  label: string;
}

/**
 * Coordinates are in a 100×150 SVG viewBox so the figure is fully scalable.
 * x=50 is the body centre line.
 *
 * The body is laid out as:
 *   Head ≈ y 3–22, Neck ≈ y 22–27, Shoulders ≈ y 28–35,
 *   Thorax ≈ y 35–48, Abdomen ≈ y 48–62, Pelvis ≈ y 62–70,
 *   Legs ≈ y 70–135, Feet ≈ y 135–148.
 */
export const BODY_REGIONS: Record<string, BodyRegion> = {
  // Head & Neck
  brain:           { x: 50, y: 10,  label: "Brain / CNS" },
  head_neck:       { x: 50, y: 22,  label: "Head & Neck" },
  thyroid:         { x: 50, y: 26,  label: "Thyroid" },

  // Thorax
  lung_left:       { x: 40, y: 42,  label: "Left Lung" },
  lung_right:      { x: 60, y: 42,  label: "Right Lung" },
  breast_left:     { x: 40, y: 45,  label: "Left Breast" },
  breast_right:    { x: 60, y: 45,  label: "Right Breast" },
  esophagus:       { x: 50, y: 38,  label: "Esophagus" },
  pleura:          { x: 36, y: 44,  label: "Pleura" },
  mediastinum:     { x: 50, y: 42,  label: "Mediastinum" },

  // Abdomen
  liver:           { x: 42, y: 52,  label: "Liver" },
  stomach:         { x: 56, y: 51,  label: "Stomach" },
  pancreas:        { x: 50, y: 55,  label: "Pancreas" },
  spleen:          { x: 60, y: 53,  label: "Spleen" },
  kidney_left:     { x: 40, y: 57,  label: "Left Kidney" },
  kidney_right:    { x: 60, y: 57,  label: "Right Kidney" },
  adrenal:         { x: 50, y: 55,  label: "Adrenal" },
  colon:           { x: 50, y: 60,  label: "Colon / Rectum" },
  small_intestine: { x: 50, y: 62,  label: "Small Intestine" },
  peritoneum:      { x: 50, y: 63,  label: "Peritoneum" },

  // Pelvis
  bladder:         { x: 50, y: 66,  label: "Bladder" },
  prostate:        { x: 50, y: 68,  label: "Prostate" },
  uterus:          { x: 50, y: 67,  label: "Uterus / Cervix" },
  ovary_left:      { x: 44, y: 65,  label: "Left Ovary" },
  ovary_right:     { x: 56, y: 65,  label: "Right Ovary" },

  // General / systemic
  bone:            { x: 32, y: 90,  label: "Bone (general)" },
  lymph_nodes:     { x: 65, y: 30,  label: "Lymph Nodes" },
  skin:            { x: 66, y: 55,  label: "Skin (general)" },
  bone_marrow:     { x: 36, y: 55,  label: "Bone Marrow" },

  // Limbs — split into left/right for better marker placement
  upper_limbs:     { x: 30, y: 48,  label: "Upper Limbs" },
  lower_limbs:     { x: 45, y: 100, label: "Lower Limbs" },
  lower_limb_left: { x: 44, y: 100, label: "Left Leg" },
  lower_limb_right:{ x: 56, y: 100, label: "Right Leg" },
  trunk:           { x: 50, y: 48,  label: "Trunk" },

  // Catch-all for "other" / unknown / unspecified primary sites.
  // Placed bottom-left so the body figure stays centred.
  other:           { x: 10, y: 140, label: "Other / Unspecified" },
};

/**
 * Cancer-domain defaults: when no explicit location is found in a field,
 * fall back to the organ most associated with the domain.
 */
const DOMAIN_DEFAULTS: Record<string, string> = {
  "medical.cancer.lung":         "lung_right",
  "medical.cancer.breast":       "breast_left",
  "medical.cancer.colorectal":   "colon",
  "medical.cancer.skin":         "skin",
  "medical.cancer.stomach":      "stomach",
  "medical.cancer.pancreatic":   "pancreas",
  "medical.cancer.liver":        "liver",
  "medical.cancer.kidney":       "kidney_right",
  "medical.cancer.bladder":      "bladder",
  "medical.cancer.prostate":     "prostate",
  "medical.cancer.thyroid":      "thyroid",
  "medical.cancer.brain":        "brain",
  "medical.cancer.cervical":     "uterus",
  "medical.cancer.ovarian":      "ovary_left",
  "medical.cancer.endometrial":  "uterus",
  "medical.cancer.esophageal":   "esophagus",
  "medical.cancer.bone":         "bone",
  "medical.cancer.sarcoma":      "trunk",
  "medical.cancer.mesothelioma": "pleura",
  "medical.cancer.cholangiocarcinoma": "liver",
  "medical.cancer.neuroendocrine": "pancreas",
  "medical.cancer.salivary":     "head_neck",
  "medical.cancer.small_intestine": "small_intestine",
  "medical.cancer.head_neck":    "head_neck",
};

/**
 * Alias map: domain-specific location values -> BODY_REGIONS keys.
 * Covers values from common_choices.py across all cancer domains.
 */
const LOCATION_ALIASES: Record<string, string> = {
  // Generic metastasis sites
  bone: "bone", liver: "liver", lung: "lung_right", brain: "brain",
  cns: "brain", lymph_nodes: "lymph_nodes", peritoneum: "peritoneum",
  adrenal: "adrenal", skin: "skin", pleura: "pleura",
  bone_marrow: "bone_marrow",
  hepatic: "liver", pulmonary: "lung_right",

  // Skin / general
  head: "head_neck", face: "head_neck", scalp: "head_neck", neck: "head_neck",
  trunk: "trunk", chest: "trunk", back: "trunk", abdomen: "colon",
  arm: "upper_limbs", shoulder: "upper_limbs", forearm: "upper_limbs",
  hand: "upper_limbs", finger: "upper_limbs",
  thigh: "lower_limbs", leg: "lower_limbs", foot: "lower_limbs", toe: "lower_limbs",
  axilla: "lymph_nodes", inguinal: "lymph_nodes",
  acral: "lower_limbs", mucosa: "head_neck",
  lower_limbs: "lower_limbs", upper_limbs: "upper_limbs",
  "head and neck": "head_neck", "head_and_neck": "head_neck",

  // Breast
  left_breast: "breast_left", right_breast: "breast_right",
  upper_outer_left: "breast_left", upper_inner_left: "breast_left",
  lower_outer_left: "breast_left", lower_inner_left: "breast_left",
  upper_outer_right: "breast_right", upper_inner_right: "breast_right",
  lower_outer_right: "breast_right", lower_inner_right: "breast_right",
  central_left: "breast_left", central_right: "breast_right",

  // Lung
  right_lung: "lung_right", left_lung: "lung_left",
  rul: "lung_right", rml: "lung_right", rll: "lung_right",
  lul: "lung_left", lll: "lung_left",
  mediastinum: "mediastinum",

  // Colorectal
  cecum: "colon", ascending_colon: "colon", hepatic_flexure: "colon",
  transverse_colon: "colon", splenic_flexure: "colon", descending_colon: "colon",
  sigmoid_colon: "colon", rectosigmoid: "colon", rectum: "colon",
  upper_rectum: "colon", mid_rectum: "colon", lower_rectum: "colon",
  appendix: "colon",

  // Stomach
  cardia: "stomach", fundus: "stomach", body_stomach: "stomach",
  antrum: "stomach", pylorus: "stomach", gej: "esophagus",
  gastroesophageal_junction: "esophagus",

  // Pancreas
  head_pancreas: "pancreas", body_pancreas: "pancreas", tail_pancreas: "pancreas",
  uncinate: "pancreas",

  // Esophageal
  upper_esophagus: "esophagus", middle_esophagus: "esophagus",
  lower_esophagus: "esophagus", cervical_esophagus: "esophagus",

  // Kidney
  left_kidney: "kidney_left", right_kidney: "kidney_right",

  // Bladder
  bladder: "bladder", urethra: "bladder",

  // Reproductive
  prostate: "prostate", uterus: "uterus", cervix: "uterus",
  ovary: "ovary_left", left_ovary: "ovary_left", right_ovary: "ovary_right",
  endometrium: "uterus", fallopian_tube: "ovary_left",

  // Bone locations
  femur: "lower_limbs", tibia: "lower_limbs", fibula: "lower_limbs",
  humerus: "upper_limbs", radius: "upper_limbs", ulna: "upper_limbs",
  pelvis: "bladder", sacrum: "colon", spine: "trunk", vertebra: "trunk",
  rib: "trunk", sternum: "trunk", scapula: "upper_limbs", clavicle: "head_neck",
  skull: "brain",

  // Brain
  frontal: "brain", temporal: "brain", parietal: "brain", occipital: "brain",
  cerebellum: "brain", brainstem: "brain", thalamus: "brain",
  pituitary: "brain", pineal: "brain", ventricle: "brain",
  spinal_cord: "trunk",

  // Thyroid
  thyroid: "thyroid", left_lobe: "thyroid", right_lobe: "thyroid", isthmus: "thyroid",

  // Catch-all
  other: "other", unknown: "other", unspecified: "other",
  not_specified: "other", na: "other", none: "other",
  "asked-unknown": "other", "asked_unknown": "other",
};

/**
 * Map a domain-specific location value to a BODY_REGIONS key.
 *
 * Resolution order:
 * 1. Direct key match in BODY_REGIONS
 * 2. Alias lookup
 * 3. Substring match against BODY_REGIONS keys
 * 4. Substring match against alias keys
 * 5. Domain default
 * 6. "other" (catch-all fallback)
 */
export function mapLocationToRegion(
  location: string,
  cancerDomain?: string,
): string | null {
  if (!location) return cancerDomain ? (DOMAIN_DEFAULTS[cancerDomain] ?? null) : null;

  const norm = location.toLowerCase().trim().replace(/[\s-]+/g, "_");

  // 1. Direct match
  if (norm in BODY_REGIONS) return norm;

  // 2. Alias
  if (norm in LOCATION_ALIASES) return LOCATION_ALIASES[norm];

  // 3. Substring against region keys
  for (const region of Object.keys(BODY_REGIONS)) {
    if (norm.includes(region) || region.includes(norm)) return region;
  }

  // 4. Substring against alias keys
  for (const [alias, region] of Object.entries(LOCATION_ALIASES)) {
    if (norm.includes(alias)) return region;
  }

  // 5. Domain default
  if (cancerDomain && cancerDomain in DOMAIN_DEFAULTS) {
    return DOMAIN_DEFAULTS[cancerDomain];
  }

  return null;
}
